import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { snackbar } from '@occc-app/design-system';
import { Segment_Upload_Mode } from '@occc/graphql';

import { SegmentUploadFile } from './segment-upload-file.component';

import { SegmentUploadActionModal } from '../features/segment-upload-action-modal';
import { useSegmentUploadFileController } from '../hooks/use-segment-upload-file.controller.hook';
import {
  SEGMENT_UPLOAD_FILE_SIZE_ERROR,
  SEGMENT_UPLOAD_MAX_FILE_SIZE_BYTES,
} from '../segments.constants';

type SegmentUploadFileControllerProps = {
  segmentUuid: string;
  shouldDisableControls?: boolean;
  isSmallProgressIndicator?: boolean;
  hasExistingFiles?: boolean;
  hasIncompleteFiles?: boolean;
};

export const SegmentUploadFileController = ({
  segmentUuid,
  shouldDisableControls = false,
  isSmallProgressIndicator,
  hasExistingFiles = false,
  hasIncompleteFiles = false,
}: SegmentUploadFileControllerProps) => {
  const { uploadFile, uploadedFileState } = useSegmentUploadFileController({
    segmentUuid,
  });

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showErrorMessage = (message?: string) => {
    snackbar.error(
      message ||
        'Only a single CSV file is accepted. Please ensure the file has the correct format.',
    );
  };

  const handleUploadWithMode = useCallback(
    (file: File, mode: Segment_Upload_Mode) => {
      uploadFile(file, mode);
    },
    [uploadFile],
  );

  const handleModalConfirm = useCallback(
    (mode: Segment_Upload_Mode) => {
      if (pendingFile) {
        handleUploadWithMode(pendingFile, mode);
        setPendingFile(null);
      }
    },
    [pendingFile, handleUploadWithMode],
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setPendingFile(null);
  }, []);

  const onDrop = useCallback(
    (files: File[]) => {
      if (hasIncompleteFiles) {
        showErrorMessage(
          'Another file is currently being processed. Please wait until it finishes before uploading a new one',
        );
        return;
      }

      if (files.length !== 1) {
        showErrorMessage();
        return;
      }

      const file = files[0];

      if (file.size > SEGMENT_UPLOAD_MAX_FILE_SIZE_BYTES) {
        showErrorMessage(SEGMENT_UPLOAD_FILE_SIZE_ERROR);
        return;
      }

      if (hasExistingFiles) {
        setPendingFile(file);
        setIsModalOpen(true);
      } else {
        handleUploadWithMode(file, Segment_Upload_Mode.Add);
      }
    },
    [hasExistingFiles, handleUploadWithMode, hasIncompleteFiles],
  );

  const isDisabled = shouldDisableControls || uploadedFileState !== null || hasIncompleteFiles;

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    noDrag: isDisabled,
  });

  return (
    <>
      <SegmentUploadFile
        uploadedFileState={uploadedFileState}
        shouldDisableControls={isDisabled}
        dragAnDropAreaProps={getRootProps()}
        hiddenFileInputProps={getInputProps()}
        onBrowseFilesClick={open}
        isSmallProgressIndicator={isSmallProgressIndicator}
      />
      <SegmentUploadActionModal
        open={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
      />
    </>
  );
};
